/**
 * Investments Service
 *
 * API endpoints for investment operations
 */

import { apiClient } from '../client';

import type { ApiResponse, InvestmentFilterParams } from '../types';
import type { Investment, InvestmentWithRelations } from '@medsuite/models';

export interface CreateInvestmentDto {
  portco_id: number;
  segment_id: number;
  title: string;
  is_regulatory_requirement: boolean;
  regulatory_requirement_details?: string;
  prime_objective?: string;
  problem_context?: string;
  improvement_ideas?: string;
  assumptions?: string;
}

export interface UpdateInvestmentDto extends Partial<CreateInvestmentDto> {
  score_override?: number | null;
}

export interface InvestmentSummary extends Investment {
  milestones_count: number;
  sponsors_count: number;
  resources_count: number;
  costs_total: number;
  benefits_count: number;
  total_score: number;
}

/**
 * Investments Service
 */
class InvestmentsService {
  /**
   * Get all investments with optional filters
   */
  async getAll(params?: InvestmentFilterParams): Promise<ApiResponse<InvestmentWithRelations[]>> {
    return apiClient.get<InvestmentWithRelations[]>('/investments', { params });
  }

  /**
   * Get approval queue (investments with status 'submitted')
   */
  async getQueue(): Promise<ApiResponse<InvestmentWithRelations[]>> {
    return apiClient.get<InvestmentWithRelations[]>('/investments/queue');
  }

  /**
   * Get single investment by ID
   */
  async getById(id: number): Promise<ApiResponse<Investment>> {
    return apiClient.get<Investment>(`/investments/${id}`);
  }

  /**
   * Get investment summary with aggregated data
   */
  async getSummary(id: number): Promise<ApiResponse<InvestmentSummary>> {
    return apiClient.get<InvestmentSummary>(`/investments/${id}/summary`);
  }

  /**
   * Create new investment
   */
  async create(data: CreateInvestmentDto): Promise<ApiResponse<Investment>> {
    return apiClient.post<Investment>('/investments', data);
  }

  /**
   * Update investment
   */
  async update(id: number, data: UpdateInvestmentDto): Promise<ApiResponse<Investment>> {
    return apiClient.put<Investment>(`/investments/${id}`, data);
  }

  /**
   * Delete investment
   */
  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/investments/${id}`);
  }

  /**
   * Update score override
   */
  async updateScoreOverride(
    id: number,
    scoreOverride: number | null
  ): Promise<ApiResponse<Investment>> {
    return apiClient.patch<Investment>(`/investments/${id}/score-override`, {
      score_override: scoreOverride,
    });
  }
}

export const investmentsService = new InvestmentsService();
